import React, { useState } from 'react';
import { Card, CardBody, Button } from './BaseComponents';
import { changePassword } from '../services/accountService';

interface PasswordChangeData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface PasswordStrength {
    score: number; // 0-4
    message: string;
    color: string;
}

/**
 * PasswordChangeForm - Form component for changing user password
 *
 * Features:
 * - Current password verification
 * - New password strength indicator
 * - Password confirmation matching
 * - Client-side validation
 * - Form submission handling with loading states
 */
export const PasswordChangeForm: React.FC = () => {
    const [formData, setFormData] = useState<PasswordChangeData>({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const checkPasswordStrength = (password: string): PasswordStrength => {
        if (password.length === 0) {
            return { score: 0, message: '', color: 'text-neutral-400' };
        }

        let score = 0;
        const checks = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };

        // Calculate score
        if (checks.length) score++;
        if (checks.uppercase) score++;
        if (checks.lowercase) score++;
        if (checks.number) score++;
        if (checks.special) score++;

        const messages = [
            { message: 'Very weak', color: 'text-red-500' },
            { message: 'Weak', color: 'text-red-400' },
            { message: 'Fair', color: 'text-yellow-500' },
            { message: 'Good', color: 'text-blue-500' },
            { message: 'Strong', color: 'text-green-500' }
        ];

        return {
            score,
            message: messages[score].message,
            color: messages[score].color
        };
    };

    const validateForm = (): string | null => {
        if (!formData.currentPassword) {
            return 'Current password is required';
        }
        if (!formData.newPassword) {
            return 'New password is required';
        }
        if (formData.newPassword.length < 8) {
            return 'New password must be at least 8 characters long';
        }
        if (formData.newPassword === formData.currentPassword) {
            return 'New password must be different from current password';
        }
        if (formData.newPassword !== formData.confirmPassword) {
            return 'New passwords do not match';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationError = validateForm();
        if (validationError) {
            setMessage({ type: 'error', text: validationError });
            return;
        }

        setIsLoading(true);
        setMessage(null);

        try {
            const response = await changePassword({
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword,
                confirmPassword: formData.confirmPassword
            });

            setMessage({ type: 'success', text: response.message });
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (error: any) {
            setMessage({
                type: 'error',
                text: error.message || 'Failed to change password. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const passwordStrength = checkPasswordStrength(formData.newPassword);

    return (
        <Card noHover={true}>
            <CardBody>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Current Password */}
                    <div>
                        <label htmlFor="currentPassword" className="block text-sm font-medium text-neutral-700 mb-2">
                            Current Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.current ? 'text' : 'password'}
                                id="currentPassword"
                                value={formData.currentPassword}
                                onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('current')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                            >
                                {showPasswords.current ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                    </div>

                    {/* New Password */}
                    <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium text-neutral-700 mb-2">
                            New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.new ? 'text' : 'password'}
                                id="newPassword"
                                value={formData.newPassword}
                                onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('new')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                            >
                                {showPasswords.new ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>

                        {/* Password Strength Indicator */}
                        {formData.newPassword && (
                            <div className="mt-2">
                                <div className="flex items-center gap-1 mb-1">
                                    {[...Array(5)].map((_, i) => (
                                        <div
                                            key={i}
                                            className={`h-1 w-full rounded ${
                                                i < passwordStrength.score
                                                    ? passwordStrength.score <= 1 ? 'bg-red-500'
                                                    : passwordStrength.score <= 2 ? 'bg-yellow-500'
                                                    : passwordStrength.score <= 3 ? 'bg-blue-500'
                                                    : 'bg-green-500'
                                                    : 'bg-neutral-200'
                                            }`}
                                        />
                                    ))}
                                </div>
                                <p className={`text-xs ${passwordStrength.color}`}>
                                    {passwordStrength.message}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-2">
                            Confirm New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.confirm ? 'text' : 'password'}
                                id="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent ${
                                    formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                                        ? 'border-red-300'
                                        : 'border-neutral-300'
                                }`}
                                required
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('confirm')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                            >
                                {showPasswords.confirm ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                            <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                        )}
                    </div>

                    {/* Message Display */}
                    {message && (
                        <div className={`p-3 rounded-lg text-sm ${
                            message.type === 'success'
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                            {message.text}
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="pt-4">
                        <Button
                            type="submit"
                            disabled={isLoading || !!validateForm()}
                            fullWidth
                        >
                            {isLoading ? 'Changing Password...' : 'Change Password'}
                        </Button>
                    </div>

                    {/* Password Requirements */}
                    <div className="text-xs text-neutral-500 space-y-1">
                        <p className="font-medium">Password Requirements:</p>
                        <ul className="space-y-1 ml-4">
                            <li>• At least 8 characters long</li>
                            <li>• Include uppercase and lowercase letters</li>
                            <li>• Include at least one number</li>
                            <li>• Include at least one special character</li>
                        </ul>
                    </div>
                </form>
            </CardBody>
        </Card>
    );
};